import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MemoirSummary, RenewalKind } from '../../../../types'
import { apiClient } from '../lib/api-client'
import { ApiError } from '../lib/errors'
import { SUPPORT_EMAIL } from '../lib/legal'
import { formatMemoirDate } from './memoir-payment-notice'

/** Set on the renewal success URL, so the page knows the buyer just paid. */
const RENEWED_PARAM = 'renewed'
const RECHECK_MS = 3_000
const RECHECK_LIMIT = 2
/** Mirrors the worker's RENEWAL_OPENS_BEFORE_MS (90 days); only decides whether to ask for a price. */
const WINDOW_MS = 90 * 24 * 60 * 60 * 1000

type Price = { readOnlyEur: number; writableEur: number; opensAt: string }

/**
 * Another year for a Memoir (SOL-185), under its name in the workspace.
 *
 * - "Keep it for another year": read-only, offered once the renewal window is
 *   open (90 days before the year ends) or the Memoir has lapsed;
 * - "Keep adding photos": writable, offered then too, and whenever uploads are
 *   closed.
 *
 * Prices come from the server, which sizes the Memoir; this never shows a size.
 * The refund of a renewal is the existing refund button in the payment notice.
 * A Memoir waiting to be paid for again (refund grace) is not renewed, and
 * neither is one with an open invitation: it says it passes to the client.
 */
export default function MemoirRenewal({
  memoir,
  onChanged,
}: {
  memoir: MemoirSummary
  onChanged?: () => void
}): JSX.Element | null {
  const [price, setPrice] = useState<Price | null>(null)
  const [busy, setBusy] = useState<RenewalKind | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const renewed = searchParams.get(RENEWED_PARAM) === '1'

  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged

  const refundGrace = memoir.status === 'grace' && !memoir.lapsed
  const maybeOpen = !!memoir.lapsed || !memoir.writable
    || Date.now() >= Date.parse(memoir.paidUntil) - WINDOW_MS
  const wantsPrice = !refundGrace && !memoir.invite && maybeOpen

  useEffect(() => {
    if (!wantsPrice) return
    let cancelled = false
    apiClient.getRenewalPrice(memoir.uuid)
      .then((p) => { if (!cancelled) setPrice(p) })
      .catch(() => { /* no price, no buttons: nothing is offered that cannot be priced */ })
    return () => { cancelled = true }
  }, [wantsPrice, memoir.uuid, memoir.paidUntil])

  // Back from Checkout the webhook may trail the redirect: look again twice.
  useEffect(() => {
    if (!renewed) return
    let polls = 0
    const timer = setInterval(() => {
      polls += 1
      onChangedRef.current?.()
      if (polls >= RECHECK_LIMIT) clearInterval(timer)
    }, RECHECK_MS)
    return () => clearInterval(timer)
  }, [renewed])

  // The marker has done its job once the new year shows.
  const firstPaidUntil = useRef(memoir.paidUntil)
  useEffect(() => {
    if (renewed && memoir.paidUntil !== firstPaidUntil.current) {
      setSearchParams((p) => { p.delete(RENEWED_PARAM); return p }, { replace: true })
    }
  }, [renewed, memoir.paidUntil, setSearchParams])

  async function renew(kind: RenewalKind): Promise<void> {
    setBusy(kind)
    setError(null)
    try {
      const here = `${window.location.origin}/memoir/${encodeURIComponent(memoir.uuid)}`
      const { url } = await apiClient.renewMemoir(memoir.uuid, kind, `${here}?${RENEWED_PARAM}=1`, here)
      window.location.href = url
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409
        ? e.message
        : `Could not start checkout. Please try again, or write to ${SUPPORT_EMAIL}.`)
      setBusy(null)
    }
  }

  const windowOpen = !!price && (!!memoir.lapsed || Date.now() >= Date.parse(price.opensAt))
  const showReadOnly = windowOpen
  const showWritable = !!price && (windowOpen || !memoir.writable)

  if (refundGrace) return null

  // An open invitation (SOL-185): the Memoir passes to the client, and the
  // server refuses a renewal meanwhile, so none is offered. Withdrawing the
  // invitation is how the photographer keeps it.
  if (memoir.invite) {
    return (
      <div className="memoir-payment">
        <p className="memoir-payment__message" role="status">
          {memoir.invite.acceptedAt
            ? `${memoir.name} passes to your client on ${formatMemoirDate(memoir.paidUntil)} if they've accepted; nothing to pay.`
            : `${memoir.name} passes to your client once they accept; until then it stays yours. If they don't, withdraw the invitation above to keep it yourself.`}
        </p>
      </div>
    )
  }

  const lapsedNotice = !!memoir.lapsed && !!memoir.deletionDueAt
  if (!renewed && !lapsedNotice && !showReadOnly && !showWritable) return null

  return (
    <div className="memoir-payment">
      {renewed && (
        <p className="memoir-payment__message" role="status">
          Payment received, thank you. The new year shows here as soon as the payment is confirmed,
          usually within a minute; for a bank transfer or direct debit it can take a few days.
        </p>
      )}
      {memoir.lapsed && memoir.deletionDueAt && (
        <p className="memoir-payment__message" role="status">
          The year for this Memoir has ended, so it is read-only and will be deleted with
          everything in it on {formatMemoirDate(memoir.deletionDueAt)} unless renewed.
        </p>
      )}
      {!renewed && price && (
        <>
          {showReadOnly && (
            <button type="button" className="memoir-payment__action" disabled={busy !== null}
              onClick={() => void renew('read_only')}>
              Keep it for another year — €{price.readOnlyEur} + VAT
            </button>
          )}
          {showWritable && (
            <button type="button" className="memoir-payment__action" disabled={busy !== null}
              onClick={() => void renew('writable')}>
              Keep adding photos — €{price.writableEur} + VAT
            </button>
          )}
        </>
      )}
      {error && <p className="memoir-payment__error" role="alert">{error}</p>}
    </div>
  )
}
